import offlineStorage from '@/utils/offlineStorage'
import { EventBus } from '@/utils/eventBus'
import blobService from '@/services/blobService'

class FileUploadService {
  constructor () {
    this.uploadQueue = []
    this.isProcessing = false
    this.maxConcurrentUploads = 2
    this.activeUploads = 0
  }

  /**
   * Queue a file for upload
   * @param {Object} file - File object with data, name, size, status
   * @param {String} idKoppeling - ID to link the file to (e.g., idSchatting)
   * @param {String} type - Type of file (referentieType)
   * @param {String} subtype - Subtype (optional)
   * @returns {Number} Upload ID
   */
  async queueFileUpload (file, idKoppeling, type, subtype = null) {
    const fileRecord = {
      idKoppeling,
      type,
      subtype,
      fileName: file.name,
      fileSize: file.size,
      fileData: file.data, // Base64 data (already resized if image)
      blobDataString: file.data,
      idBlob: file.idBlob || null,
      blobStatus: file.status,
      uploadStatus: 'pending',
      uploadProgress: 0,
      timestamp: Date.now(),
      retryCount: 0
    }

    const id = await offlineStorage.addFileUpload(fileRecord)

    EventBus.$emit('file-queued', { id, fileName: file.name, idKoppeling })

    // Start processing queue if online
    if (navigator.onLine) {
      this.processQueue()
    }

    return id
  }

  /**
   * Queue multiple files for upload
   * @param {Array} files - Array of file objects
   * @param {String} idKoppeling - ID to link files to
   * @param {String} type - Type of files
   * @param {String} subtype - Subtype (optional)
   * @returns {Array} Array of upload IDs
   */
  async queueMultipleFiles (files, idKoppeling, type, subtype = null) {
    const uploadIds = []

    for (const file of files) {
      // Only queue new files
      if (file.status === 1) { // status.new
        const id = await this.queueFileUpload(file, idKoppeling, type, subtype)
        uploadIds.push(id)
      }
    }

    return uploadIds
  }

  /**
   * Process the upload queue
   */
  async processQueue () {
    if (this.isProcessing) {
      // Already processing, just return - new files will be picked up
      return
    }

    this.isProcessing = true

    try {
      while (true) {
        const pendingFiles = await offlineStorage.getPendingFileUploads()

        if (pendingFiles.length === 0) {
          // No more files to process
          break
        }

        EventBus.$emit('upload-processing-started')

        const results = {
          total: pendingFiles.length,
          success: 0,
          failed: 0
        }

        const uploadPromises = []

        for (const fileRecord of pendingFiles) {
          // Respect concurrent upload limit
          while (this.activeUploads >= this.maxConcurrentUploads) {
            await new Promise(resolve => setTimeout(resolve, 100))
          }

          // Upload file and track promise
          const uploadPromise = this.uploadFile(fileRecord).then(success => {
            if (success) {
              results.success++
            } else {
              results.failed++
            }
            return success
          }).catch(error => {
            console.error('Upload error:', error)
            results.failed++
            return false
          })

          uploadPromises.push(uploadPromise)
        }

        // Wait for all uploads in this batch to complete
        await Promise.all(uploadPromises)

        EventBus.$emit('upload-processing-completed', results)

        // Check if new files were added while processing
        const newPendingFiles = await offlineStorage.getPendingFileUploads()
        if (newPendingFiles.length === 0) {
          break
        }
      }
    } catch (error) {
      console.error('Error processing upload queue:', error)
      EventBus.$emit('upload-processing-error', error)
    } finally {
      this.isProcessing = false
    }
  }

  /**
   * Upload a single file using the existing Blob/Save endpoint
   * @param {Object} fileRecord - File record from IndexedDB
   * @returns {Boolean} Success status
   */
  async uploadFile (fileRecord) {
    this.activeUploads++

    try {
      await offlineStorage.updateFileUploadStatus(fileRecord.id, 'uploading')

      EventBus.$emit('file-upload-started', {
        id: fileRecord.id,
        fileName: fileRecord.fileName,
        idKoppeling: fileRecord.idKoppeling
      })

      // Images are already resized before queueing, no need to resize here
      // Prepare blob object for backend (matches existing format)
      const blob = {
        idBlob: fileRecord.idBlob,
        idKoppeling: fileRecord.idKoppeling,
        type: fileRecord.type,
        subtype: fileRecord.subtype,
        blobDataString: fileRecord.blobDataString,
        blobStatus: fileRecord.blobStatus,
        size: fileRecord.fileSize,
        bestandsnaam: fileRecord.fileName
      }

      // Use existing blobService.Save - it expects an array
      const response = await blobService.Save([blob])

      if (response && response.data && response.data.length > 0) {
        const savedBlob = response.data[0]

        await offlineStorage.updateFileUploadStatus(fileRecord.id, 'completed', {
          uploadProgress: 100,
          serverBlobId: savedBlob.idBlob,
          uploadedAt: Date.now()
        })

        EventBus.$emit('file-uploaded', {
          id: fileRecord.id,
          fileName: fileRecord.fileName,
          idKoppeling: fileRecord.idKoppeling,
          serverBlobId: savedBlob.idBlob
        })

        return true
      } else {
        throw new Error('Upload response invalid')
      }
    } catch (error) {
      console.error('File upload failed:', error)

      const newRetryCount = fileRecord.retryCount + 1

      if (newRetryCount < 3) {
        // Retry up to 3 times
        await offlineStorage.updateFileUploadStatus(fileRecord.id, 'pending', {
          retryCount: newRetryCount,
          lastError: error.message
        })

        EventBus.$emit('file-upload-retry', {
          id: fileRecord.id,
          fileName: fileRecord.fileName,
          retryCount: newRetryCount
        })
      } else {
        // Give up after 3 retries
        await offlineStorage.updateFileUploadStatus(fileRecord.id, 'failed', {
          retryCount: newRetryCount,
          lastError: error.message
        })

        EventBus.$emit('file-upload-failed', {
          id: fileRecord.id,
          fileName: fileRecord.fileName,
          error: error.message
        })
      }

      return false
    } finally {
      this.activeUploads--
    }
  }

  /**
   * Get upload status for a specific idKoppeling (e.g., idSchatting)
   * @param {String} idKoppeling
   * @returns {Array} Upload records
   */
  async getUploadStatus (idKoppeling) {
    return await offlineStorage.getFileUploadsByKoppeling(idKoppeling)
  }

  /**
   * Get overall upload statistics
   * @returns {Object} Stats object with pending, uploading, completed, failed counts
   */
  async getUploadStats () {
    return await offlineStorage.getFileUploadStats()
  }

  /**
   * Retry failed uploads
   */
  async retryFailedUploads () {
    const failedUploads = await offlineStorage.getFileUploadsByStatus('failed')

    for (const upload of failedUploads) {
      await offlineStorage.updateFileUploadStatus(upload.id, 'pending', {
        retryCount: 0
      })
    }

    if (navigator.onLine) {
      this.processQueue()
    }
  }

  /**
   * Cancel a pending upload
   * @param {Number} uploadId
   */
  async cancelUpload (uploadId) {
    await offlineStorage.deleteFileUpload(uploadId)
    EventBus.$emit('file-upload-cancelled', { id: uploadId })
  }

  /**
   * Clear completed uploads
   */
  async clearCompletedUploads () {
    await offlineStorage.cleanupCompletedUploads(0) // Clear all completed
  }
}

export default new FileUploadService()
